import React, { useEffect, useRef, useMemo } from "react";
import { useParams } from "react-router-dom";
import { Box, Button, Card, CheckboxField, DateField, TextField, Typography } from "@curves/ui-base";
import { CyclePurchaseSummaryCard, TerminatePageSection, SelectWithMenuItems } from "@icoach/healthyCyclePurchase/components";
import { useCyclePurchaseEffectOrderData, useCyclePurchaseEffectOrderSubmit, useCyclePurchaseEffectRange } from "@icoach/healthyCyclePurchase/hooks";
import { getErrorModuleStatus, refIsRequiredError } from "@util/utils";
import useInactivityTimerToggle from "@util/hook/useInactivityTimerToggle";
import { EmployeeSelect, FocusModeToggle } from "@icoach/components";
import { Grid } from "@mui/material";
import { getNowTime, initialDate, isValidDate, parseDate } from "@util/moment";
import { EFFECT_CONTENT_TYPE } from "@constants/";

/**
 * 循環訂單終止申請頁面
 * 獨立申請表單，使用變更單 API
 */
const CyclePurchaseTerminatePage = () => {
	const { cycleOrderID, effectID = 0 } = useParams();

	// 使用 hooks 管理資料
	const { cycleOrder, effectOrder, resource, memberID, clubID, shippingDay } = useCyclePurchaseEffectOrderData({
		cycleOrderID,
		effectID,
	});

	// 提交變更單 hook
	const { submit, validationErrors, clearValidationErrors } = useCyclePurchaseEffectOrderSubmit({
		effectID,
		memberID,
	});

	// 使用 hook 取得變更單的生效日期和下次出貨日期
	const { effectDate, getEffectRangeDebounced, lastShippingDate } = useCyclePurchaseEffectRange();

	// refs
	const unsubscribeReasonRef = useRef(null);
	const unsubscribeNoteRef = useRef(null);
	const unsubscribeConfirmRef = useRef(null);
	const termRef = useRef(null);
	const effectCreateDateRef = useRef(null);
	const effectDateRef = useRef(null);
	const employeeRef = useRef(null);

	// 聚焦模式 ref
	const focusModeRef = useRef(false);

	useInactivityTimerToggle();

	// 根據 effectID 決定預設值來源：effectID = 0 使用 cycleOrder，否則使用 effectOrder
	const defaultSource = useMemo(() => {
		const isNewEffectOrder = Number(effectID) === 0;
		return isNewEffectOrder ? cycleOrder : effectOrder;
	}, [effectID, cycleOrder, effectOrder]);

	const getParams = () => {
		let result = {
			effectType: [EFFECT_CONTENT_TYPE.QUIT], // 終止申請的 effectType
			effectDate,
			shippingDay, // 提交時帶入原本的出貨日設定
			clubID,
			memberID,
			cycleOrderID,
		};

		if (unsubscribeReasonRef.current && unsubscribeReasonRef.current.getResult) {
			result.unsubscribeReason = unsubscribeReasonRef.current.getResult();
		}

		if (unsubscribeNoteRef.current && unsubscribeNoteRef.current.getResult) {
			result.unsubscribeNote = unsubscribeNoteRef.current.getResult();
		}

		if (termRef.current && termRef.current.getResult) {
			result = Object.assign(result, termRef.current.getResult());
		}

		if (effectCreateDateRef.current && effectCreateDateRef.current.getResult) {
			result.effectCreateDate = effectCreateDateRef.current.getResult();
		}

		if (effectDateRef.current && effectDateRef.current.getResult) {
			result.effectDate = effectDateRef.current.getResult();
		}

		if (employeeRef.current && employeeRef.current.getResult) {
			result.employeeID = employeeRef.current.getResult() || 0;
		}

		return result;
	};

	const handleEffectDateChange = (_, date) => {
		// 變更 effectDate 時，重新計算 effectDate 和 lastShippingDate
		if (isValidDate(date)) {
			const params = getParams();
			getEffectRangeDebounced({
				...params,
				effectOrder: date,
			});
		}
	};

	const handleSubmit = () => {
		clearValidationErrors();
		const isError = refIsRequiredError(unsubscribeReasonRef, unsubscribeNoteRef, unsubscribeConfirmRef, termRef, effectCreateDateRef, effectDateRef, employeeRef);
		if (!isError) {
			const params = getParams();
			submit(params);
		}
	};

	// 當表單資料變化時，自動計算 effectDate 和 lastShippingDate
	useEffect(() => {
		if (isValidDate(effectDate)) {
			const params = getParams();
			getEffectRangeDebounced(params);
		}
		// eslint-disable-next-line
	}, [effectDate]);

	return (
		<Box className="container main-container-spacing cycle-purchase-terminate-page">
			<Box component="header" className="page-header">
				<Typography className="page-header-title" variant="h3">
					健康產品變更單
				</Typography>
			</Box>
			<FocusModeToggle ref={focusModeRef}>
				<CyclePurchaseSummaryCard sourceData={cycleOrder} />
				<Card className={"order-info-card mb-4"}>
					<Card.CardHeader title="申請退訂" className="bg-light-active" />
					<Card.CardContent className={"p-4"}>
						<Grid spacing={3} container>
							<Grid xs={6} item>
								<SelectWithMenuItems
									label="退訂原因"
									ref={unsubscribeReasonRef}
									key={defaultSource?.unsubscribeReason}
									defaultValue={defaultSource?.unsubscribeReason}
									options={resource?.unsubscribeReasonOptions ?? []}
									{...getErrorModuleStatus(validationErrors, "unsubscribeReason")}
									fullWidth
									required
								/>
							</Grid>
							<Grid xs={12} item>
								<TextField
									label="退訂原因補充說明"
									ref={unsubscribeNoteRef}
									key={defaultSource?.unsubscribeNote}
									defaultValue={defaultSource?.unsubscribeNote}
									{...getErrorModuleStatus(validationErrors, "unsubscribeNote")}
									minRows={3}
									multiline
									fullWidth
								/>
							</Grid>
							{isValidDate(lastShippingDate) && (
								<Grid xs={12} item>
									<CheckboxField ref={unsubscribeConfirmRef} required>
										<CheckboxField.Checkbox value={true} label={`我已確認將於${parseDate(lastShippingDate, "YYYY [年] M [月]")}，收到最後乙次出貨。`} />
									</CheckboxField>
								</Grid>
							)}
							<Grid xs={4} item>
								<DateField
									label="建單日期"
									ref={effectCreateDateRef}
									key={initialDate(defaultSource?.effectCreateDate)}
									defaultValue={initialDate(defaultSource?.effectCreateDate) || initialDate(getNowTime())}
									{...getErrorModuleStatus(validationErrors, "effectCreateDate")}
									fullWidth
									required
								/>
							</Grid>
							<Grid xs={4} item>
								<DateField
									label="變更生效日期"
									ref={effectDateRef}
									key={initialDate(effectDate)}
									defaultValue={initialDate(effectDate)}
									onChange={handleEffectDateChange}
									{...getErrorModuleStatus(validationErrors, "effectDate")}
									fullWidth
									required
								/>
							</Grid>
							<Grid xs={4} item>
								<EmployeeSelect label="擔當" ref={employeeRef} options={resource?.coachOptions ?? []} {...getErrorModuleStatus(validationErrors, "employeeID")} fullWidth required />
							</Grid>
						</Grid>
					</Card.CardContent>
				</Card>
				{/* 訂購條款 */}
				<TerminatePageSection className={"mb-4"} ref={termRef} lastShippingDate={lastShippingDate} errorModule={validationErrors} />
				<Box className={"text-right btn-group px-4 pb-3"}>
					<Button variant={"contained"} onClick={() => focusModeRef.current.checkAndExitFocusMode(handleSubmit)}>
						確認送出
					</Button>
				</Box>
			</FocusModeToggle>
		</Box>
	);
};

export default CyclePurchaseTerminatePage;
